<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames, wrapInArray } from '@components/utils';

import { makeDimensionProps, makeThemeProps, provideTheme, useDimensions, useRender } from '../../composable';

import { allowedTypes } from './types';

import type { TSkeletonBones, TSkeletonLoaderType } from './types';
import type { PropType } from 'vue';

function generateStructure(type?: string): TSkeletonBones {
  let children: TSkeletonBones = [];

  if (!type) return children;

  const bone = (allowedTypes as Record<string, string>)[type];

  if (type === bone) {
    // End of recursion, do nothing
  } else if (type.includes(',')) {
    // Array of values - e.g. 'heading, paragraph, text@2'
    return mapBones(type);
  } else if (type.includes('@')) {
    // Array of values - e.g. 'paragraph@4'
    return generateBones(type);
  } else if (bone.includes(',')) {
    // Array of values - e.g. 'card@2'
    children = mapBones(bone);
  } else if (bone.includes('@')) {
    // Array of values - e.g. 'list-item@2'
    children = generateBones(bone);
  } else if (bone) {
    // Single value - e.g. 'block'
    children.push(generateStructure(bone));
  }

  return [generateBone(type, children)];
}

function generateBone(type: string, children: TSkeletonBones = []) {
  return h('div', { class: classNames('cds-skeleton-loader__bone', `cds-skeleton-loader__${type}`) }, children);
}

function generateBones(bone: string) {
  const [type, length] = bone.split('@') as [TSkeletonLoaderType, number];

  // Generate a length array based upon
  // value after @ in the bone string
  return Array.from({ length }).map(() => generateStructure(type));
}

function mapBones(bones: string) {
  return bones.replace(/\s/g, '').split(',').map(generateStructure);
}

export const componentName = 'CdsSkeletonLoader';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Тип скелета
     */
    type: {
      type: [String, Array] as PropType<
        TSkeletonLoaderType | (string & {}) | ReadonlyArray<TSkeletonLoaderType | (string & {})>
      >,
      default: 'block'
    },
    /**
     * Показывать ли скелет при загрузке
     */
    loading: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Текст для aria label во время загрузки
     */
    loadingText: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Использовать скелет в виде заготовки для прототипирования
     */
    boilerplate: {
      type: Boolean as PropType<boolean>,
      default: false
    },

    ...makeDimensionProps(),
    ...makeThemeProps()
  },
  emits: {},
  setup(props, { slots }) {
    const { dimensionsStyles } = useDimensions(props);
    const { themeClasses } = provideTheme(props);

    const items = computed(() => generateStructure(wrapInArray(props.type as TSkeletonLoaderType).join(',')));

    useRender(() => {
      const isLoading = !slots.default || props.loading;

      return h(
        'div',
        {
          class: classNames('cds-skeleton-loader', unref(themeClasses), {
            'cds-skeleton-loader--is-loading': isLoading,
            'cds-skeleton-loader--boilerplate': props.boilerplate
          }),
          style: isLoading ? unref(dimensionsStyles) : {},
          role: !props.boilerplate ? 'alert' : undefined,
          'aria-busy': !props.boilerplate ? isLoading : undefined,
          'aria-live': !props.boilerplate ? 'polite' : undefined,
          'aria-label': !props.boilerplate ? props.loadingText : undefined
        },
        isLoading ? unref(items) : slots.default?.()
      );
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsSkeletonLoader';
</style>
